import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Save, Loader2, RefreshCw, RotateCcw, Image as ImageIcon, X } from "lucide-react";
import NewsletterPanel from "@/components/NewsletterPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyHero = {
    title_line1: "",
    title_line2: "",
    title_line3: "",
    tagline: "",
    est_label: "",
    audience_label: "",
    image_url: "",
};

const formatError = (d) =>
    !d
        ? "Something went wrong."
        : typeof d === "string"
            ? d
            : Array.isArray(d)
                ? d.map((e) => e?.msg || "").filter(Boolean).join(" ")
                : d?.msg || "Something went wrong.";

// Body is stored as an array of paragraph strings — the textarea uses
// blank-line separation for readability and we split/join on save.
const bodyToText = (arr) => (Array.isArray(arr) ? arr.join("\n\n") : "");
const textToBody = (t) =>
    (t || "")
        .split(/\n\s*\n/)
        .map((p) => p.trim())
        .filter(Boolean);

export default function HomeCopyTab({ token }) {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [hero, setHero] = useState(emptyHero);
    const [chapters, setChapters] = useState([]);
    const [status, setStatus] = useState(null);
    const [uploadingImage, setUploadingImage] = useState(false);
    const fileInputRef = useRef(null);

    const headers = { Authorization: `Bearer ${token}` };

    const load = useCallback(async () => {
        setLoading(true);
        setStatus(null);
        try {
            const r = await axios.get(`${API}/site/home`);
            setHero({ ...emptyHero, ...(r.data.hero || {}) });
            setChapters(
                (r.data.chapters || []).map((c) => ({
                    index: c.index || "",
                    kicker: c.kicker || "",
                    title: c.title || "",
                    pull_quote: c.pull_quote || "",
                    body: Array.isArray(c.body) ? c.body : [],
                })),
            );
        } catch (e) {
            setStatus({
                type: "error",
                msg: formatError(e.response?.data?.detail),
            });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const save = async () => {
        setSaving(true);
        setStatus(null);
        try {
            await axios.put(
                `${API}/admin/site/home`,
                { hero, chapters },
                { headers },
            );
            setStatus({ type: "success", msg: "Home copy saved." });
        } catch (e) {
            setStatus({
                type: "error",
                msg: formatError(e.response?.data?.detail),
            });
        } finally {
            setSaving(false);
        }
    };

    const updateHero = (field, val) =>
        setHero((h) => ({ ...h, [field]: val }));
    const updateChapter = (i, field, val) =>
        setChapters((chs) => chs.map((c, ix) => (ix === i ? { ...c, [field]: val } : c)));

    const uploadHeroImage = async (file) => {
        if (!file) return;
        setUploadingImage(true);
        setStatus(null);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await axios.post(`${API}/admin/upload/image`, fd, {
                headers: { ...headers, "Content-Type": "multipart/form-data" },
            });
            if (r.data?.url) {
                updateHero("image_url", r.data.url);
                setStatus({
                    type: "success",
                    msg: "Image uploaded — remember to hit Save changes.",
                });
            }
        } catch (e) {
            setStatus({
                type: "error",
                msg: formatError(e.response?.data?.detail),
            });
        } finally {
            setUploadingImage(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    if (loading) {
        return (
            <div className="flex items-center gap-2 font-mono text-[13px] uppercase tracking-[0.22em] text-[color:var(--slate)]">
                <Loader2 size={14} className="animate-spin" />
                Loading home copy
            </div>
        );
    }

    return (
        <div data-testid="home-copy-tab" className="max-w-4xl">
            <div className="flex items-center justify-between gap-4 mb-8 flex-wrap">
                <div>
                    <h2 className="font-display text-2xl md:text-3xl leading-tight tracking-[-0.01em]">
                        Home page copy
                    </h2>
                    <p className="font-body text-[color:var(--charcoal)]/85 mt-2 max-w-[62ch]">
                        Edit the hero title, tagline, meta labels, and the three
                        manifesto chapters without touching code. Changes go
                        live on the public homepage as soon as you save.
                    </p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <button
                        type="button"
                        onClick={load}
                        data-testid="home-copy-reload"
                        className="pill-btn ghost"
                        style={{ padding: "0.6rem 1rem" }}
                    >
                        <RefreshCw size={13} strokeWidth={1.6} />
                        Reload
                    </button>
                    <button
                        type="button"
                        onClick={save}
                        disabled={saving}
                        data-testid="home-copy-save"
                        className="pill-btn"
                        style={{ padding: "0.6rem 1rem" }}
                    >
                        {saving ? (
                            <>
                                <Loader2 size={13} className="animate-spin" />
                                Saving
                            </>
                        ) : (
                            <>
                                <Save size={13} strokeWidth={1.6} />
                                Save changes
                            </>
                        )}
                    </button>
                </div>
            </div>

            {status && (
                <div
                    data-testid="home-copy-status"
                    className={`mb-6 p-3 border font-body text-sm ${
                        status.type === "success"
                            ? "border-[color:var(--charcoal)] bg-[color:var(--charcoal)]/5"
                            : "border-red-800 text-red-900"
                    }`}
                >
                    {status.msg}
                </div>
            )}

            {/* Hero */}
            <section className="mb-12 border border-[color:var(--charcoal)]/20 p-6 md:p-8">
                <h3 className="font-display text-xl md:text-2xl mb-6">
                    Hero
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <Field
                        label="Title line 1"
                        testId="hero-line1"
                        value={hero.title_line1}
                        onChange={(v) => updateHero("title_line1", v)}
                        help="Displayed first. Regular weight."
                    />
                    <Field
                        label="Title line 2 (italic)"
                        testId="hero-line2"
                        value={hero.title_line2}
                        onChange={(v) => updateHero("title_line2", v)}
                        help="Rendered italic in the hero."
                    />
                    <Field
                        label="Title line 3"
                        testId="hero-line3"
                        value={hero.title_line3}
                        onChange={(v) => updateHero("title_line3", v)}
                        help="Displayed last."
                    />
                </div>
                <TextArea
                    label="Tagline"
                    testId="hero-tagline"
                    value={hero.tagline}
                    onChange={(v) => updateHero("tagline", v)}
                    rows={3}
                    help="Sits to the right of the title. Line breaks preserved."
                />

                {/* Hero image */}
                <div className="mt-6 mb-4">
                    <span className="block font-mono font-semibold text-[12px] uppercase tracking-[0.22em] text-[color:var(--slate)] mb-2">
                        Hero image
                    </span>
                    <div className="flex flex-col sm:flex-row gap-4 items-start">
                        <div
                            data-testid="hero-image-preview"
                            className="w-full sm:w-64 aspect-[16/10] border border-[color:var(--charcoal)]/20 bg-[color:var(--charcoal)]/5 flex items-center justify-center overflow-hidden shrink-0"
                        >
                            {hero.image_url ? (
                                <img
                                    src={hero.image_url}
                                    alt="Hero preview"
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <img
                                    src="/assets/hero_globe.jpg"
                                    alt="Hero default"
                                    className="w-full h-full object-cover opacity-70"
                                />
                            )}
                        </div>
                        <div className="flex-1">
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                onChange={(e) =>
                                    uploadHeroImage(e.target.files?.[0])
                                }
                                data-testid="hero-image-file"
                                className="hidden"
                            />
                            <div className="flex flex-wrap gap-2">
                                <button
                                    type="button"
                                    onClick={() =>
                                        fileInputRef.current?.click()
                                    }
                                    disabled={uploadingImage}
                                    data-testid="hero-image-upload"
                                    className="pill-btn"
                                    style={{ padding: "0.55rem 1rem" }}
                                >
                                    {uploadingImage ? (
                                        <>
                                            <Loader2
                                                size={13}
                                                className="animate-spin"
                                            />
                                            Uploading
                                        </>
                                    ) : (
                                        <>
                                            <ImageIcon size={13} strokeWidth={1.6} />
                                            {hero.image_url
                                                ? "Replace image"
                                                : "Upload image"}
                                        </>
                                    )}
                                </button>
                                {hero.image_url && (
                                    <button
                                        type="button"
                                        onClick={() => updateHero("image_url", "")}
                                        data-testid="hero-image-reset"
                                        className="pill-btn ghost"
                                        style={{ padding: "0.55rem 1rem" }}
                                    >
                                        <X size={13} strokeWidth={1.6} />
                                        Reset to default
                                    </button>
                                )}
                            </div>
                            <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[color:var(--slate)]/80 mt-3">
                                JPEG / PNG / WebP · uploaded images replace the
                                default globe on the public homepage the moment
                                you hit Save.
                            </p>
                            {hero.image_url && (
                                <p className="font-body text-xs text-[color:var(--charcoal)]/70 mt-2 break-all">
                                    {hero.image_url}
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <Field
                        label="Meta label · left"
                        testId="hero-est-label"
                        value={hero.est_label}
                        onChange={(v) => updateHero("est_label", v)}
                        help='e.g. "Est. 2025"'
                    />
                    <Field
                        label="Meta label · right"
                        testId="hero-audience-label"
                        value={hero.audience_label}
                        onChange={(v) => updateHero("audience_label", v)}
                        help='e.g. "For Canadians"'
                    />
                </div>
            </section>

            {/* Chapters */}
            <section className="mb-4">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-display text-xl md:text-2xl">
                        Manifesto chapters
                    </h3>
                    <button
                        type="button"
                        onClick={() =>
                            setChapters((chs) => [
                                ...chs,
                                {
                                    index: String(chs.length + 1).padStart(2, "0"),
                                    kicker: "",
                                    title: "",
                                    pull_quote: "",
                                    body: [],
                                },
                            ])
                        }
                        data-testid="home-copy-add-chapter"
                        className="pill-btn ghost"
                        style={{ padding: "0.5rem 0.9rem" }}
                    >
                        + Add chapter
                    </button>
                </div>

                {chapters.map((c, i) => (
                    <div
                        key={i}
                        data-testid={`chapter-editor-${i}`}
                        className="mb-6 border border-[color:var(--charcoal)]/20 p-6 md:p-8"
                    >
                        <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
                            <span className="font-mono font-semibold text-[12px] uppercase tracking-[0.24em] text-[color:var(--slate)]">
                                Chapter · {c.index || i + 1}
                            </span>
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={() =>
                                        setChapters((chs) => {
                                            if (i === 0) return chs;
                                            const next = [...chs];
                                            [next[i - 1], next[i]] = [next[i], next[i - 1]];
                                            return next;
                                        })
                                    }
                                    data-testid={`chapter-editor-${i}-up`}
                                    disabled={i === 0}
                                    className="pill-btn ghost disabled:opacity-40"
                                    style={{ padding: "0.4rem 0.8rem" }}
                                >
                                    ↑ Move up
                                </button>
                                <button
                                    type="button"
                                    onClick={() =>
                                        setChapters((chs) => {
                                            if (i === chs.length - 1) return chs;
                                            const next = [...chs];
                                            [next[i], next[i + 1]] = [next[i + 1], next[i]];
                                            return next;
                                        })
                                    }
                                    data-testid={`chapter-editor-${i}-down`}
                                    disabled={i === chapters.length - 1}
                                    className="pill-btn ghost disabled:opacity-40"
                                    style={{ padding: "0.4rem 0.8rem" }}
                                >
                                    ↓ Move down
                                </button>
                                <button
                                    type="button"
                                    onClick={() =>
                                        setChapters((chs) => chs.filter((_, ix) => ix !== i))
                                    }
                                    data-testid={`chapter-editor-${i}-remove`}
                                    className="pill-btn ghost"
                                    style={{
                                        padding: "0.4rem 0.8rem",
                                        color: "#7a1c1c",
                                        borderColor: "#7a1c1c",
                                    }}
                                >
                                    Remove
                                </button>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                            <Field
                                label="Number"
                                testId={`chapter-${i}-index`}
                                value={c.index}
                                onChange={(v) => updateChapter(i, "index", v)}
                                help='e.g. "01"'
                            />
                            <Field
                                label="Kicker"
                                testId={`chapter-${i}-kicker`}
                                value={c.kicker}
                                onChange={(v) => updateChapter(i, "kicker", v)}
                                help='e.g. "The Premise"'
                            />
                        </div>
                        <Field
                            label="Title"
                            testId={`chapter-${i}-title`}
                            value={c.title}
                            onChange={(v) => updateChapter(i, "title", v)}
                        />
                        <TextArea
                            label="Pull quote"
                            testId={`chapter-${i}-pullquote`}
                            value={c.pull_quote}
                            onChange={(v) => updateChapter(i, "pull_quote", v)}
                            rows={2}
                            help="Bolded quotation rendered inline."
                        />
                        <TextArea
                            label="Body paragraphs"
                            testId={`chapter-${i}-body`}
                            value={bodyToText(c.body)}
                            onChange={(v) => updateChapter(i, "body", textToBody(v))}
                            rows={8}
                            help="Separate paragraphs with a blank line."
                        />
                    </div>
                ))}
            </section>

            <div className="flex gap-2 sticky bottom-6 py-4 backdrop-blur bg-[color:var(--sky-base)]/60 -mx-2 px-2 border-t border-[color:var(--charcoal)]/15">
                <button
                    type="button"
                    onClick={save}
                    disabled={saving}
                    data-testid="home-copy-save-bottom"
                    className="pill-btn"
                    style={{ padding: "0.7rem 1.3rem" }}
                >
                    {saving ? (
                        <>
                            <Loader2 size={14} className="animate-spin" />
                            Saving
                        </>
                    ) : (
                        <>
                            <Save size={14} strokeWidth={1.6} />
                            Save changes
                        </>
                    )}
                </button>
                <button
                    type="button"
                    onClick={load}
                    data-testid="home-copy-revert"
                    className="pill-btn ghost"
                    style={{ padding: "0.7rem 1.3rem" }}
                >
                    <RotateCcw size={14} strokeWidth={1.6} />
                    Discard changes
                </button>
            </div>

            {/* Newsletter broadcast — sits at the bottom of Home Copy so a
                new chapter can be composed above and dispatched below. */}
            <NewsletterPanel token={token} />
        </div>
    );
}

const Field = ({ label, testId, value, onChange, help }) => (
    <label className="block mb-3">
        <span className="block font-mono font-semibold text-[12px] uppercase tracking-[0.22em] text-[color:var(--slate)] mb-1.5">
            {label}
        </span>
        <input
            type="text"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            data-testid={testId}
            className="w-full bg-transparent border border-[color:var(--charcoal)]/25 focus:border-[color:var(--charcoal)] outline-none px-3 py-2 font-body text-base"
        />
        {help && (
            <span className="block font-mono text-[11px] uppercase tracking-[0.18em] text-[color:var(--slate)]/80 mt-1">
                {help}
            </span>
        )}
    </label>
);

const TextArea = ({ label, testId, value, onChange, rows = 3, help }) => (
    <label className="block mb-3">
        <span className="block font-mono font-semibold text-[12px] uppercase tracking-[0.22em] text-[color:var(--slate)] mb-1.5">
            {label}
        </span>
        <textarea
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            data-testid={testId}
            rows={rows}
            className="w-full bg-transparent border border-[color:var(--charcoal)]/25 focus:border-[color:var(--charcoal)] outline-none px-3 py-2 font-body text-base resize-y"
        />
        {help && (
            <span className="block font-mono text-[11px] uppercase tracking-[0.18em] text-[color:var(--slate)]/80 mt-1">
                {help}
            </span>
        )}
    </label>
);
